import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Container,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Snackbar,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  FitnessCenter as FitnessIcon,
  Person as PersonIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { format, parseISO, isPast, isFuture, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const MyBookingsPage = () => {
  const { t } = useTranslation('myBookings');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [facilityBookings, setFacilityBookings] = useState([]);
  const [classBookings, setClassBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelDialog, setCancelDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingType, setBookingType] = useState(''); // 'facility' or 'class'
  const [cancelLoading, setCancelLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Filter and pagination state
  const [mainTab, setMainTab] = useState(0); // 0: Facilities, 1: Classes
  const [timeFilter, setTimeFilter] = useState('all'); // all, upcoming, past
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const itemsPerPage = 6;

  useEffect(() => {
    if (user) {
      fetchBookings();
    }
  }, [user, mainTab, timeFilter, statusFilter, currentPage]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      
      if (mainTab === 0) {
        // Fetch facility bookings
        await fetchFacilityBookings();
      } else {
        // Fetch class bookings
        await fetchClassBookings();
      }
    } catch (err) {
      setError(t('messages.error.fetchFailed'));
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilityBookings = async () => {
    const params = {
      page: currentPage,
      limit: itemsPerPage
    };

    // Add filters based on time filter
    if (timeFilter === 'upcoming') {
      params.upcoming = 'true';
    } else if (timeFilter === 'past') {
      params.past = 'true';
    }

    if (statusFilter) {
      params.status = statusFilter;
    }

    const response = await api.get('/bookings/my', { params });
    if (response.data && response.data.success) {
      setFacilityBookings(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalBookings(response.data.pagination?.total || 0);
      setError('');
    } else {
      setFacilityBookings([]);
      setTotalPages(1);
      setTotalBookings(0);
      setError('');
    }
  };

  const fetchClassBookings = async () => {
    const params = {
      page: currentPage,
      limit: itemsPerPage
    };

    // Add filters based on time filter
    if (timeFilter === 'upcoming') {
      params.upcoming = 'true';
    } else if (timeFilter === 'past') {
      params.past = 'true';
    }

    if (statusFilter) {
      params.status = statusFilter;
    }

    const response = await api.get('/scheduled-classes/my/bookings', { params });
    if (response.data && response.data.success) {
      setClassBookings(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalBookings(response.data.pagination?.total || 0);
      setError('');
    } else {
      setClassBookings([]);
      setTotalPages(1);
      setTotalBookings(0);
      setError('');
    }
  };

  const handleCancelFacilityBooking = async (booking) => {
    try {
      // Check if booking can be cancelled
      const response = await api.get(`/bookings/${booking._id}/can-cancel`);
      
      if (!response.data.data.canCancel) {
        setSnackbar({
          open: true,
          message: response.data.data.reason || t('messages.error.cannotCancel'),
          severity: 'warning'
        });
        return;
      }
      
      setSelectedBooking(booking);
      setBookingType('facility');
      setCancelDialog(true);
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('messages.error.cancelCheckFailed');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleCancelClassBooking = (booking) => {
    setSelectedBooking(booking);
    setBookingType('class');
    setCancelDialog(true);
  };

  const confirmCancelBooking = async () => {
    if (!selectedBooking || !bookingType) return;

    try {
      setCancelLoading(true);
      
      if (bookingType === 'facility') {
        await api.put(`/bookings/${selectedBooking._id}/cancel`);
      } else {
        await api.delete(`/scheduled-classes/${selectedBooking._id}/unbook`);
      }
      
      setSnackbar({
        open: true,
        message: bookingType === 'facility' ? t('messages.success.facilityCancelled') : t('messages.success.classCancelled'),
        severity: 'success'
      });
      
      setCancelDialog(false);
      setSelectedBooking(null);
      setBookingType('');
      
      // Refresh bookings
      fetchBookings();
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('messages.error.cancelFailed');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      console.error('Error cancelling booking:', err);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDialogClose = () => {
    setCancelDialog(false);
    setSelectedBooking(null);
    setBookingType('');
  };

  const handleMainTabChange = (event, newValue) => {
    setMainTab(newValue);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  const handleTimeFilterChange = (event) => {
    setTimeFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
      case 'booked':
        return <CheckCircleIcon color="success" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'completed':
      case 'attended':
        return <CheckCircleIcon color="primary" />;
      case 'no-show':
        return <ErrorIcon color="warning" />;
      default:
        return <PendingIcon color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'booked':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'completed':
      case 'attended':
        return 'primary';
      case 'no-show':
        return 'warning';
      default:
        return 'info';
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = parseISO(dateTimeString);
    return {
      date: format(date, 'EEEE, MMMM d, yyyy'),
      time: format(date, 'h:mm a')
    };
  };

  const getBookingTimeStatus = (startTime, endTime) => {
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    const now = new Date();

    if (isPast(end)) return 'past';
    if (isToday(start)) return 'today';
    if (isFuture(start)) return 'upcoming';
    return 'current';
  };

  const canCancelFacilityBooking = (booking) => {
    if (booking.status !== 'confirmed') return false;
    
    const startTime = parseISO(booking.startTime);
    const now = new Date();
    
    // Can cancel if booking is at least 1 hour in the future (legacy behavior)
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    return startTime > oneHourFromNow;
  };


  const canCancelClassBooking = (booking) => {
    if (booking.status === 'cancelled' || booking.status === 'completed') return false;
    
    const startTime = parseISO(booking.startTime);
    const now = new Date();
    
    // Can cancel if class is at least 1 hour in the future
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    return startTime > oneHourFromNow;
  };

  const getCurrentBookings = () => {
    return mainTab === 0 ? facilityBookings : classBookings;
  };

  const renderFacilityBookingCard = (booking) => {
    const startDateTime = formatDateTime(booking.startTime);
    const endDateTime = formatDateTime(booking.endTime);
    const timeStatus = getBookingTimeStatus(booking.startTime, booking.endTime);
    
    return (
      <Grid size={{xs:12 , md:6}} key={booking._id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {/* Status Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1
            }}
          >
            <Chip
              icon={getStatusIcon(booking.status)}
              label={booking.status}
              color={getStatusColor(booking.status)}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>

          <CardContent sx={{ flexGrow: 1, pr: 10 }}>
            {/* Facility Name */}
            <Typography variant="h6" component="h3" gutterBottom>
              {booking.facility.name}
            </Typography>

            {/* Facility Type */}
            <Chip
              label={booking.facility.type}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize', mb: 2 }}
            />

            {/* Date and Time */}
            <List dense sx={{ py: 0 }}>
              <ListItem sx={{ px: 0 }}>
                <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 'small' }} />
                <ListItemText
                  primary={t('facilityBookings.labels.date')}
                  secondary={startDateTime.date}
                />
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <TimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 'small' }} />
                <ListItemText
                  primary={t('facilityBookings.labels.time')}
                  secondary={`${startDateTime.time} - ${endDateTime.time}`}
                />
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <ScheduleIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 'small' }} />
                <ListItemText
                  primary={t('facilityBookings.labels.bookingId')}
                  secondary={booking._id.slice(-8).toUpperCase()}
                />
              </ListItem>
            </List>

            {/* Notes */}
            {booking.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('facilityBookings.labels.notes')}:</strong> {booking.notes}
                </Typography>
              </Box>
            )}

            {/* Time Status Indicator */}
            <Box sx={{ mt: 2 }}>
              <Chip
                label={
                  timeStatus === 'today' ? 'Today' :
                  timeStatus === 'upcoming' ? 'Upcoming' :
                  timeStatus === 'past' ? 'Past' : 'Current'
                }
                size="small"
                color={
                  timeStatus === 'today' ? 'warning' :
                  timeStatus === 'upcoming' ? 'info' :
                  timeStatus === 'past' ? 'default' : 'success'
                }
                variant="outlined"
              />
            </Box>
          </CardContent>

          {/* Actions */}
          {canCancelFacilityBooking(booking) && (
            <Box sx={{ p: 2, pt: 0 }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => handleCancelFacilityBooking(booking)}
                fullWidth
              >
                {t('facilityBookings.actions.cancelBooking')}
              </Button>
            </Box>
          )}
        </Card>
      </Grid>
    );
  };

  const renderClassBookingCard = (booking) => {
    const startDateTime = formatDateTime(booking.startTime);
    const endDateTime = formatDateTime(booking.endTime);
    const timeStatus = getBookingTimeStatus(booking.startTime, booking.endTime);
    
    // Get user's attendance status
    const userAttendee = booking.attendees?.find(attendee => 
      attendee.user._id === user.id || attendee.user === user.id
    );
    const attendanceStatus = userAttendee?.status || 'booked';
    
    return (
      <Grid size={{xs:12 , md:6}} key={booking._id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {/* Status Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1
            }}
          >
            <Chip
              icon={getStatusIcon(attendanceStatus)}
              label={attendanceStatus}
              color={getStatusColor(attendanceStatus)}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>

          <CardContent sx={{ flexGrow: 1, pr: 10 }}>
            {/* Class Name */}
            <Typography variant="h6" component="h3" gutterBottom>
              {booking.classType?.name}
            </Typography>

            {/* Class Category */}
            {booking.classType?.category && (
              <Chip
                label={booking.classType.category}
                size="small"
                variant="outlined"
                sx={{ textTransform: 'capitalize', mb: 2 }}
              />
            )}

            {/* Date and Time */}
            <List dense sx={{ py: 0 }}>
              <ListItem sx={{ px: 0 }}>
                <ScheduleIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 'small' }} />
                <ListItemText
                  primary={t('classBookings.labels.date')}
                  secondary={startDateTime.date}
                />
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <TimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 'small' }} />
                <ListItemText
                  primary={t('classBookings.labels.time')}
                  secondary={`${startDateTime.time} - ${endDateTime.time}`}
                />
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 'small' }} />
                <ListItemText
                  primary={t('classBookings.labels.trainer')}
                  secondary={`${booking.trainer?.firstName} ${booking.trainer?.lastName}`}
                />
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 'small' }} />
                <ListItemText
                  primary={t('classBookings.labels.location')}
                  secondary={booking.facility?.name}
                />
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <GroupIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 'small' }} />
                <ListItemText
                  primary={t('classBookings.labels.capacity')}
                  secondary={t('classBookings.labels.enrolled', { current: booking.currentEnrollment || 0, max: booking.capacity })}
                />
              </ListItem>
            </List>

            {/* Time Status Indicator */}
            <Box sx={{ mt: 2 }}>
              <Chip
                label={
                  timeStatus === 'today' ? 'Today' :
                  timeStatus === 'upcoming' ? 'Upcoming' :
                  timeStatus === 'past' ? 'Past' : 'Current'
                }
                size="small"
                color={
                  timeStatus === 'today' ? 'warning' :
                  timeStatus === 'upcoming' ? 'info' :
                  timeStatus === 'past' ? 'default' : 'success'
                }
                variant="outlined"
              />
            </Box>
          </CardContent>

          {/* Actions */}
          <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={() => navigate(`/classes/${booking._id}`)}
              sx={{ flex: 1 }}
            >
              {t('classBookings.actions.viewDetails')}
            </Button>
            {canCancelClassBooking(booking) && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => handleCancelClassBooking(booking)}
                sx={{ flex: 1 }}
              >
                {t('classBookings.actions.cancel')}
              </Button>
            )}
          </Box>
        </Card>
      </Grid>
    );
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          {t('messages.loginRequired')}
        </Alert>
      </Container>
    );
  }

  const currentBookings = getCurrentBookings();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Main Tabs and Filters */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={mainTab} onChange={handleMainTabChange}>
            <Tab
              icon={<LocationIcon />}
              label={`${t('tabs.facilities')} (${mainTab === 0 ? totalBookings : ''})`}
              iconPosition="start"
            />
            <Tab
              icon={<FitnessIcon />}
              label={`${t('tabs.classes')} (${mainTab === 1 ? totalBookings : ''})`}
              iconPosition="start"
            />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FilterIcon color="action" />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('filters.time')}</InputLabel>
            <Select
              value={timeFilter}
              onChange={handleTimeFilterChange}
              label={t('filters.time')}
            >
              <MenuItem value="all">{t('filters.all')}</MenuItem>
              <MenuItem value="upcoming">{t('filters.upcoming')}</MenuItem>
              <MenuItem value="past">{t('filters.past')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('filters.status')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label={t('filters.status')}
            >
              <MenuItem value="">{t('filters.allStatuses')}</MenuItem>
              {mainTab === 0 ? [
                <MenuItem key="confirmed" value="confirmed">{t('facilityBookings.status.confirmed')}</MenuItem>,
                <MenuItem key="cancelled" value="cancelled">{t('facilityBookings.status.cancelled')}</MenuItem>,
                <MenuItem key="completed" value="completed">{t('facilityBookings.status.completed')}</MenuItem>,
                <MenuItem key="no-show" value="no-show">{t('facilityBookings.status.noShow')}</MenuItem>
              ] : [
                <MenuItem key="booked" value="booked">{t('classBookings.status.booked')}</MenuItem>,
                <MenuItem key="attended" value="attended">{t('classBookings.status.attended')}</MenuItem>,
                <MenuItem key="no-show" value="no-show">{t('classBookings.status.noShow')}</MenuItem>,
                <MenuItem key="cancelled" value="cancelled">{t('classBookings.status.cancelled')}</MenuItem>
              ]}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Bookings List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : currentBookings.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Box sx={{ mb: 2 }}>
            {mainTab === 0 ? <LocationIcon sx={{ fontSize: 64, color: 'text.secondary' }} /> : <FitnessIcon sx={{ fontSize: 64, color: 'text.secondary' }} />}
          </Box>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {mainTab === 0 ? t('emptyState.noFacilityBookings') : t('emptyState.noClassBookings')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {timeFilter === 'upcoming'
              ? t('emptyState.upcoming', { type: mainTab === 0 ? 'facility' : 'class' })
              : timeFilter === 'past'
              ? t('emptyState.past', { type: mainTab === 0 ? 'facility' : 'class' })
              : t('emptyState.none', { type: mainTab === 0 ? 'facility' : 'class' })
            }
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(mainTab === 0 ? '/facilities' : '/classes')}
          >
            {t('emptyState.browse', { type: mainTab === 0 ? 'Facilities' : 'Classes' })}
          </Button>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {currentBookings.map((booking) => 
              mainTab === 0 ? renderFacilityBookingCard(booking) : renderClassBookingCard(booking)
            )}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </Stack>
            </Box>
          )}
        </>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {t('cancelDialog.title', { type: bookingType === 'facility' ? 'Facility' : 'Class' })}
            <IconButton onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedBooking && (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                {t('cancelDialog.warning')}
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                {bookingType === 'facility' 
                  ? selectedBooking.facility?.name 
                  : selectedBooking.classType?.name
                }
              </Typography>
              
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('facilityBookings.labels.date')}
                    secondary={formatDateTime(selectedBooking.startTime).date}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={t('facilityBookings.labels.time')}
                    secondary={`${formatDateTime(selectedBooking.startTime).time} - ${formatDateTime(selectedBooking.endTime).time}`}
                  />
                </ListItem>
                {bookingType === 'class' && (
                  <>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={t('classBookings.labels.trainer')}
                        secondary={`${selectedBooking.trainer?.firstName} ${selectedBooking.trainer?.lastName}`}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={t('classBookings.labels.location')}
                        secondary={selectedBooking.facility?.name}
                      />
                    </ListItem>
                  </>
                )}
                {bookingType === 'facility' && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={t('facilityBookings.labels.bookingId')}
                      secondary={selectedBooking._id.slice(-8).toUpperCase()}
                    />
                  </ListItem>
                )}
              </List>
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleDialogClose}>
            {t('cancelDialog.actions.keep')}
          </Button>
          <Button
            onClick={confirmCancelBooking}
            variant="contained"
            color="error"
            disabled={cancelLoading}
          >
            {cancelLoading ? <CircularProgress size={20} /> : t('cancelDialog.actions.cancel')}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MyBookingsPage;